import { format, isValid } from 'date-fns';
import { useMemo } from 'react';

import {
  Autocomplete,
  Button,
  Checkbox,
  DatePicker,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@/ui';

import { getTimezoneOptions } from '../timezoneOptions';

import type { TimezoneOption } from '../timezoneOptions';
import type { FC } from 'react';

interface AvailabilityTrainingProps {
  executiveTrainingCompleted: boolean;
  hasVacation: boolean;
  onExecutiveTrainingChange: (value: boolean) => void;
  onHasVacationChange: (value: boolean) => void;
  onTimezoneChange: (value: string | null) => void;
  onVacationEndDateChange: (value: string | null) => void;
  onVacationEndDateError: (hasError: boolean) => void;
  onVacationStartDateChange: (value: string | null) => void;
  onVacationStartDateError: (hasError: boolean) => void;
  timezone: string | null;
  vacationEndDate: string | null;
  vacationEndDateError: boolean;
  vacationStartDate: string | null;
  vacationStartDateError: boolean;
}

const parseDatePart = (value: string | null) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return isValid(date) && format(date, 'yyyy-MM-dd') === value ? date : null;
};

const updateDate = (
  date: Date | null,
  onChange: (value: string | null) => void,
) => {
  if (!date) onChange(null);
  else if (isValid(date)) onChange(format(date, 'yyyy-MM-dd'));
};

const AvailabilityTraining: FC<AvailabilityTrainingProps> = ({
  executiveTrainingCompleted,
  hasVacation,
  onExecutiveTrainingChange,
  onHasVacationChange,
  onTimezoneChange,
  onVacationEndDateChange,
  onVacationEndDateError,
  onVacationStartDateChange,
  onVacationStartDateError,
  timezone,
  vacationEndDate,
  vacationEndDateError,
  vacationStartDate,
  vacationStartDateError,
}) => {
  const options = useMemo(() => {
    const baseOptions = getTimezoneOptions();
    return timezone && !baseOptions.some((option) => option.value === timezone)
      ? [{ label: timezone, value: timezone }, ...baseOptions]
      : baseOptions;
  }, [timezone]);
  const selectedTimezone = useMemo(
    () => options.find((option) => option.value === timezone) ?? null,
    [options, timezone],
  );
  const vacationDatesReversed = Boolean(
    vacationStartDate &&
    vacationEndDate &&
    !vacationStartDateError &&
    !vacationEndDateError &&
    vacationStartDate > vacationEndDate,
  );

  return (
    <Grid spacing={4} container>
      <Grid md={5} xs={12} item>
        <Typography className="mb-2" variant="h4">
          Availability and training
        </Typography>
        <Typography variant="body1">
          Manage the coach's timezone, informational vacation, and training.
        </Typography>
      </Grid>
      <Grid className="mt-1 flex flex-col gap-4" md={7} xs={12} item>
        <Autocomplete<TimezoneOption, false, false, false>
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          options={options}
          renderInput={(params) => <TextField {...params} label="Timezone" />}
          value={selectedTimezone}
          onChange={(_, option) => onTimezoneChange(option?.value ?? null)}
        />
        {!hasVacation ? (
          <Button variant="outlined" onClick={() => onHasVacationChange(true)}>
            Add vacation
          </Button>
        ) : (
          <div className="flex flex-col gap-3 rounded-xl border border-cement-300 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <DatePicker
                label="Vacation starts"
                slotProps={{
                  textField: {
                    error: vacationStartDateError || !vacationStartDate,
                    helperText:
                      vacationStartDateError || !vacationStartDate
                        ? 'Enter a valid start date'
                        : '',
                  },
                }}
                value={parseDatePart(vacationStartDate)}
                onChange={(date) => updateDate(date, onVacationStartDateChange)}
                onError={(reason) => onVacationStartDateError(Boolean(reason))}
              />
              <DatePicker
                label="Vacation ends"
                slotProps={{
                  textField: {
                    error: vacationEndDateError || !vacationEndDate,
                    helperText:
                      vacationEndDateError || !vacationEndDate
                        ? 'Enter a valid end date'
                        : '',
                  },
                }}
                value={parseDatePart(vacationEndDate)}
                onChange={(date) => updateDate(date, onVacationEndDateChange)}
                onError={(reason) => onVacationEndDateError(Boolean(reason))}
              />
            </div>
            {vacationDatesReversed && (
              <FormHelperText error>
                Vacation end must be on or after the start date
              </FormHelperText>
            )}
            <Button
              variant="outlined"
              onClick={() => onHasVacationChange(false)}
            >
              Remove vacation
            </Button>
          </div>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={executiveTrainingCompleted}
              onChange={(event) =>
                onExecutiveTrainingChange(event.target.checked)
              }
            />
          }
          label="Completed executive training"
        />
      </Grid>
    </Grid>
  );
};

export default AvailabilityTraining;
